import React, { FC, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { cartMobx, productMobx, restaurantConfig } from 'restaurant'
import { decorationMobx } from '@mobxStore'
import { getPlatform, handleEleScrollTo, isGif } from '@utils'
import { addParams, classNames } from '@dt/fe-utils'
import { handleShareForWxa } from '@common/utils/share'
import MktController from '@ctrls/mktController'
import { Tags } from '@cps'
import { DetailTop, ScrollScaleBar } from '../'
import defaultProduct from 'assets/images/default-longProduct.png'
import './index.scss'

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  no: string;
  imgUrl: string;
  picWidth: number;
  picHeight: number;
  name: string;
  intro?: string;
  allergys: IAllergy[];
  tags: ITag[];
  // 起售份数
  salesCnt?: number;
  statSaleCnt?: number;
  // 库存数量
  inventoryCnt?: number;
  /** 是否展示全部加料，加料过多需要显示切换箭头 */
  hasFullOperate: boolean;
  showDefaultPic?: boolean;
  isCombo?: boolean;
  isCanTogo?: boolean;
  isModal?: boolean;
  pics?: string;
  price?: number;
  scrollTop?: number;
  /** 餐品详情展示位置 0 默认顶部， 1 展示在规格下方 */
  showType?: 0 | 1;
  type?: 'product' | 'OptionalCombo' | 'FixedCombo';
}
interface IState {
  isFull: boolean;
  canCalc: boolean;
}

let _hasFullOperate = false

const DetailInfo: FC<IProps> = ({ no, imgUrl, pics, type = 'product', picWidth, salesCnt, picHeight, scrollTop, inventoryCnt, name, intro, allergys, tags, statSaleCnt, children, hasFullOperate, showDefaultPic, isCombo, isCanTogo, isModal, price, showType }) => {
  const initState: IState = {
    isFull: true,
    canCalc: false,
  }
  const { detailDecorates } = decorationMobx
  const { noToViewProductMap, noToIsSingleSaleViewProductMap } = productMobx
  const [canCalc, setCanCalc] = useState(initState.canCalc)
  const [isFull, setIsFull] = useState(initState.isFull)
  const [scaleTop, setScaleTop] = useState(0)
  const [showScrollBar, setShowScrollBar] = useState(false)

  const imgStyle = useMemo(() => {
    const imgArr = pics ? JSON.parse(pics) : []
    const width = imgArr.length ? imgArr[0].w || 1 : 1
    const height = imgArr.length ? imgArr[0].h || 1 : 1
    return {
      width: window.innerWidth,
      height: window.innerWidth * height / width
    }
  }, [pics])

  const detailStyle: any = useMemo(() => {
    const wWidth = window.innerWidth
    const wHeight = window.innerHeight
    let fTop = 124
    if (showDefaultPic) {
      imgUrl = defaultProduct
      picWidth = 830
      picHeight = 280
    }
    if (imgUrl) {
      let cHeight = imgStyle.height
      if (cHeight > wWidth) {
        if (!isCombo && !hasFullOperate) {
          fTop = wWidth
        }
        hasFullOperate = true
      }
      let mTop = cHeight > wWidth ? fTop - cHeight : fTop - wWidth
      let mHeight = (wHeight > cHeight ? wHeight - cHeight : cHeight - wHeight) - 60 - (isFull ? 0 : 110 - (cHeight > wHeight - 100 ? wHeight : cHeight))
      if (0 - mTop > cHeight || cHeight - mTop < wHeight - 100) {
        mTop = -14
        if (hasFullOperate) {
          hasFullOperate = false
        }
      }
      return {
        minHeight: mHeight,
        marginTop: isFull ? -14 : mTop,
        transition: '.05s ease-in all'
      }
    } else {
      if (canCalc) {
        const cHeight = document.querySelector(`.product-detail-${no}`)?.clientHeight
        if (cHeight) {
          const mTop = wHeight - cHeight - 80
          return {
            marginTop: mTop > 124 ? mTop : 124,
            marginBottom: -10, // 去除详情回弹后的缝隙
            minHeight: cHeight + 10
          }
        }
      }
      setTimeout(() => {
        setCanCalc(true)
      }, 300)
      return {
        position: 'absolute',
        bottom: 0
      }
    }
  }, [imgUrl, isFull, canCalc, showDefaultPic])

  const handleShare = () => {
    handleShareForWxa({
      // mode: 'card',
      mode: 'all',
      scene: 'product',
      router: addParams({
        url: `/${isCombo ? 'combo' : 'product'}/${no}`,
        params: {
          s: restaurantConfig.storeId,
          ct: restaurantConfig.codeType,
          et: cartMobx.info.eatType
        }
      }),
      title: name,
      imageUrl: imgUrl,
      price
    })
  }

  const setShareData = () => {
    const { isAndroid } = getPlatform()
    const isImgGif = isGif(imgUrl)
    MktController.initShareData({
      url: addParams({
        url: `${location.origin}/${isCombo ? 'combo' : 'product'}/${no}`,
        params: {
          b: Config.BRANDID,
          s: restaurantConfig.storeId,
          ct: restaurantConfig.codeType,
          et: cartMobx.info.eatType,
          t: restaurantConfig.tableCode || undefined,
        }
      }),
      title: name,
      img: isImgGif && isAndroid ? null : `${imgUrl}?x-oss-process=image/resize,m_fill,w_500,h_400,limit_0/auto-orient,0/quality,q_90` || ''
    })
  }

  useEffect(() => {
    if (no) {
      setTimeout(() => {
        getShowTypeStyle()
      }, 500)
    }
  }, [no])

  useEffect(() => {
    setShareData()
  }, [])

  const hasShare = useMemo(() => {
    return restaurantConfig.codeType === 'brand' && !isModal
  }, [isModal, restaurantConfig])

  useEffect(() => {
    _hasFullOperate = false
  }, [])

  useEffect(() => {
    if (!_hasFullOperate && hasFullOperate) {
      _hasFullOperate = true
    }
  }, [hasFullOperate])

  useEffect(() => {
    if (imgUrl) { // 有商品主图情况下才有效
      if (detailDecorates?.openFullNFree === 1) { // 打开大图显示
        setIsFull(true)
      } else {
        setIsFull(!_hasFullOperate)
      }
    }
  }, [detailDecorates.openFullNFree])

  const getShowTypeStyle = () => {
    console.log(type, noToViewProductMap[no].scales.length <= 1, !noToViewProductMap[no].modifierGroups.length)
    if (!showType) {
      const isDetailNoModify = type === 'product' && (noToViewProductMap[no].scales.length <= 1 && !noToViewProductMap[no].modifierGroups.length)
      if (isDetailNoModify) {
        return setShowScrollBar(false)
      }
      const winHeight = window.innerHeight
      const wrapTop = (document.querySelector(`.detail-scale-wrapper${isCombo ? '.combo' : '.product'}-${no}`) as HTMLElement)?.offsetTop || 0
      const _scaleTop = wrapTop + (imgUrl ? imgStyle.height : 0)
      if (_scaleTop > winHeight) {
        setScaleTop(_scaleTop)
        setShowScrollBar(true)
      }
    } else {
      setShowScrollBar(false)
    }
  }

  return (
    <>
      {imgUrl && <DetailTop no={no} imgUrl={imgUrl} pics={pics} imgStyle={imgStyle} onClick={() => _hasFullOperate ? setIsFull(!isFull) : null} />}
      <div className={`product-detail-wrap${!isFull ? ' has-gradient' : ''} ${type} product-detail-${no}`} style={detailStyle}>
        {imgUrl && _hasFullOperate ? <div className='show-full'><i className={`iconfont icon-chevron-${isFull ? 'up' : 'down'}`} data-cypid='productDetailShowFull' onClick={() => setIsFull(!isFull)}></i></div> : null}
        <div className='detail-info'>
          <h3 className='detail-name'>
            {name}
            {
              hasShare ? (
                <div className='btn-share'>
                  <i className='iconfont icon-fenxiang' data-cypid='productDetailShare' onClick={handleShare}></i>
                </div>
              ) : null
            }
          </h3>
          <Tags datas={[...(tags || []), ...(allergys || [])]} salesCnt={salesCnt} disableTogo={cartMobx.info.eatType !== 'dinein' && !isCanTogo} disableSingleSale={!(noToViewProductMap[no] ? noToIsSingleSaleViewProductMap[no]?.isSingleSale : true)} />
          <div className='sales-info'>
            {
              !!inventoryCnt && <p className='detail-month-sale-info sales'>{i18n.t('menu.salesCnt', { cnt: inventoryCnt })}</p>
            }
            {
              !!statSaleCnt && <p className='detail-month-sale-info'>{i18n.t('menu.monthSaleCnt', { cnt: statSaleCnt })}</p>
            }
          </div>
        </div>
        <div className='detail-zoom-wrapper'>
          <div className={classNames('detail-introduce-wrapper', { showDeep: showType })}>
            {intro && intro !== '<p></p>' ? <div className='detail-introduce braft-output-content' dangerouslySetInnerHTML={{ __html: intro }}></div> : null}
          </div>
          <div className={`detail-scale-wrapper ${isCombo ? 'combo' : 'product'}-${no}`}>
            {children}
          </div>
        </div>
      </div>
      {showScrollBar && (
        <ScrollScaleBar
          showType={showType}
          targetTop={scaleTop}
          nowTop={scrollTop}
          type={type}
          onChange={() => {
            handleEleScrollTo?.({
              selector: '.detail-page-modal ion-content',
              y: scaleTop
            })
          }}
        />
      )}
    </>
  )
}

export default observer(DetailInfo)
